import {
	Box,
	Button,
	Flex,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	SimpleGrid,
	Text,
	Tooltip,
	useDisclosure,
} from '@chakra-ui/react';
import { faSlidersH } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form, Formik } from 'formik';
import moment from 'moment';
import 'moment/locale/en-gb';
import 'moment/locale/km';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import c from '../../../constant';
import { getQueryString } from '../../../containers/MarketDevelopment/Utils';
import useStore from '../../../store';
import { mappingKhmerNumber } from '../../../utils/market-development';
import SelectValueFormField from '../../form/SelectValueFormField';
import LabelDate from '../../LabelDate';
import MonthRangeFormField from '../../form/MonthRangeFormField';
import { isValidMonth, isValidYear } from '../../../utils/validation.js';

const validationSchema = Yup.object().shape({
	month: Yup.string().nullable(),
	year: Yup.string().nullable(),
	monthRange: Yup.object()
		.shape({
			from: Yup.string(),
			to: Yup.string(),
		})
		.test('validSelection', 'Both "From" and "To" fields must be selected', function (value) {
			if ((value?.from && !value?.to) || (!value?.from && value?.to)) {
				return this.createError({ message: 'Both "From" and "To" fields must be selected' });
			}
			return true;
		})
		.test('validRange', '"To" month must be greater than or equal to "From" month', function (value) {
			if (value?.from && value?.to) {
				const fromMonth = parseInt(value?.from);
				const toMonth = parseInt(value?.to);
				if (toMonth < fromMonth) {
					return this.createError({ message: '"To" month must be greater than or equal to "From" month' });
				}
			}
			return true;
		})
		.nullable(),
});

export default function MonthlySummarizedFilterSubmit({ tab = null, month = null, year = null, monthFrom = null, monthTo = null, onCallback = () => null }) {
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const { isOpen, onOpen, onClose } = useDisclosure();

	return (
		<>
			<Tooltip
				label={
					<>
						{tab === 'Total' && (
							<Box>
								{isValidMonth(monthFrom) && isValidMonth(monthTo) && (
									<>
										{monthFrom === monthTo && (
											<Flex justifyContent="flex-start" alignItems="center">
												<Text fontWeight="bold" color="white" mr={1}>
													{t('Month')}
												</Text>
												<LabelDate input={parseInt(monthFrom) + 1} inputFormat="M" outputFormat="MMMM" color="white" />
											</Flex>
										)}
										{monthFrom !== monthTo && (
											<Flex justifyContent="flex-start" alignItems="center">
												<Text fontWeight="bold" color="white" mr={1}>
													{t('Month')}
												</Text>
												<LabelDate input={parseInt(monthFrom) + 1} inputFormat="M" outputFormat="MMMM" color="white" />
												<Text color="white" mx={2}>
													{t('to')}
												</Text>
												<LabelDate input={parseInt(monthTo) + 1} inputFormat="M" outputFormat="MMMM" color="white" />
											</Flex>
										)}
									</>
								)}
								<Flex justifyContent="flex-start" alignItems="center">
									<Text fontWeight="bold" color="white" mr={1}>
										{t('Year')}
									</Text>
									<LabelDate input={isValidYear(year) ? parseInt(year) : moment().year()} inputFormat="Y" outputFormat="YYYY" color="white" />
								</Flex>
							</Box>
						)}
						{tab !== 'Total' && (
							<Box>
								{isValidMonth(month) && (
									<Flex justifyContent="flex-start" alignItems="center">
										<Text fontWeight="bold" color="white" mr={1}>
											{t('Month')}
										</Text>
										<LabelDate input={parseInt(month) + 1} inputFormat="M" outputFormat="MMMM" color="white" />
									</Flex>
								)}
								<Flex justifyContent="flex-start" alignItems="center">
									<Text fontWeight="bold" color="white" mr={1}>
										{t('Year')}
									</Text>
									<LabelDate input={isValidYear(year) ? parseInt(year) : moment().year()} inputFormat="Y" outputFormat="YYYY" color="white" />
								</Flex>
							</Box>
						)}
					</>
				}
			>
				<Button
					isLoading={false}
					isDisabled={false}
					onClick={onOpen}
					leftIcon={<FontAwesomeIcon icon={faSlidersH} style={{ fontSize: 16 }} />}
					colorScheme={
						tab === 'Total'
							? (isValidMonth(monthFrom) && isValidMonth(monthTo)) || isValidYear(year)
								? 'yellow'
								: 'gray'
							: isValidMonth(month) || isValidYear(year)
								? 'yellow'
								: 'gray'
					}
					variant="solid"
					size="sm"
					borderRadius={c.borderRadius}
					border="1px solid #bdc3c7"
				>
					<Text fontSize="sm" fontWeight="semibold">
						{t('Filter')}
					</Text>
				</Button>
			</Tooltip>
			<Modal borderRadius={c.borderRadius} isOpen={isOpen} onClose={onClose}>
				<ModalOverlay />
				<ModalContent borderRadius={c.borderRadius}>
					<ModalHeader>{t('Filter')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Formik
							validationSchema={validationSchema}
							initialValues={{
								month,
								year,
								monthRange: { from: monthFrom ?? '', to: monthTo ?? '' },
							}}
							onSubmit={(values) => {
								const params = {
									tab,
									month: values?.month,
									year: values?.year,
									monthFrom: values?.monthRange?.from,
									monthTo: values?.monthRange?.to,
								};
								const queryString = getQueryString(params);
								const url = '?' + queryString;
								onCallback(url);
								onClose();
							}}
						>
							{({ values, setFieldValue, ...props }) => {
								return (
									<Box pb="4">
										<Form>
											<SimpleGrid columns={1} spacing={4}>
												{tab === 'Total' && <MonthRangeFormField {...props} name="monthRange" label="Month" />}
												{tab !== 'Total' && (
													<SelectValueFormField
														{...props}
														placeholder={t('Month')}
														name="month"
														label="Month"
														options={[
															{ label: lang === 'kh' ? 'មករា' : 'January', value: 0 },
															{ label: lang === 'kh' ? 'កុម្ភៈ' : 'February', value: 1 },
															{ label: lang === 'kh' ? 'មីនា' : 'March', value: 2 },
															{ label: lang === 'kh' ? 'មេសា' : 'April', value: 3 },
															{ label: lang === 'kh' ? 'ឧសភា' : 'May', value: 4 },
															{ label: lang === 'kh' ? 'មិថុនា' : 'June', value: 5 },
															{ label: lang === 'kh' ? 'កក្កដា' : 'July', value: 6 },
															{ label: lang === 'kh' ? 'សីហា' : 'August', value: 7 },
															{ label: lang === 'kh' ? 'កញ្ញា' : 'September', value: 8 },
															{ label: lang === 'kh' ? 'តុលា' : 'October', value: 9 },
															{ label: lang === 'kh' ? 'វិច្ឆិកា' : 'November', value: 10 },
															{ label: lang === 'kh' ? 'ធ្នូ' : 'December', value: 11 },
														]}
													/>
												)}
												<SelectValueFormField
													{...props}
													placeholder={t('Year')}
													name="year"
													label="Year"
													options={[0].reduce((p, c) => {
														let lp = p;
														let i = 2021;
														while (i <= moment().year()) {
															lp.push({ label: lang === 'kh' ? mappingKhmerNumber(i) : i, value: i });
															i = i + 1;
														}
														return lp?.reverse();
													}, [])}
												/>
												<Flex mt={2} justifyContent="flex-end">
													<Button
														mr={2}
														colorScheme="gray"
														variant="solid"
														size="md"
														h="42px"
														w="128px"
														borderRadius={c.borderRadius}
														onMouseDown={() => {
															const params = { tab };
															const queryString = getQueryString(params);
															const url = '?' + queryString;
															onCallback(url);
															onClose();
														}}
													>
														{t('Clear')}
													</Button>
													<Button
														ml={2}
														colorScheme="blue"
														variant="solid"
														size="md"
														h="42px"
														w="128px"
														type="submit"
														borderRadius={c.borderRadius}
													>
														{t('Apply')}
													</Button>
												</Flex>
											</SimpleGrid>
										</Form>
									</Box>
								);
							}}
						</Formik>
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	);
}
